/**
 * Turning the operator's chosen wall-clock time into the timestamp the API
 * stores.
 *
 * The picker hands back a local shape, "YYYY-MM-DDTHH:mm(:ss)", with no zone
 * on it. The API wants RFC 3339 WITH an offset, and the schedule fires against
 * Thai business hours, so the chosen time is read as Bangkok time — never as
 * the browser's zone. A laptop left on UTC would otherwise queue a publish
 * seven hours off the time its owner typed, and nothing on screen would say so.
 */

/** Thailand has one fixed offset and no daylight saving, so a constant is the
 *  whole truth here — no zone database needed. */
export const BANGKOK_OFFSET = "+07:00";
export const BANGKOK_ZONE_LABEL = "Asia/Bangkok (GMT+07:00)";

/** "YYYY-MM-DDTHH:mm" with optional seconds — what the date-time picker emits. */
const LOCAL_SHAPE = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})(?::(\d{2}))?$/;

/**
 * Local picker value → the RFC 3339 timestamp the API takes, read as Bangkok
 * time. Returns "" for an empty or malformed value, which callers treat as
 * "nothing chosen" rather than sending a guess.
 */
export const toScheduledAt = (local: string | null | undefined): string => {
  const parts = LOCAL_SHAPE.exec((local ?? "").trim());
  if (!parts) return "";
  const [, date, hhmm, ss] = parts;
  return `${date}T${hhmm}:${ss ?? "00"}${BANGKOK_OFFSET}`;
};

/**
 * Whether the chosen time is still ahead of `now`.
 *
 * The comparison runs on the absolute instant the offset pins down, so it is
 * the same answer in every browser zone — and it matches the API, which
 * refuses a past `scheduledAt` with a 400.
 */
export const isFutureSchedule = (
  local: string | null | undefined,
  now: Date = new Date(),
): boolean => {
  const scheduledAt = toScheduledAt(local);
  if (!scheduledAt) return false;
  const chosen = Date.parse(scheduledAt);
  return Number.isFinite(chosen) && chosen > now.getTime();
};

/**
 * A stored `scheduledAt` as the list writes dates elsewhere: "DD/MM/YYYY HH:mm"
 * in Bangkok time, so a schedule reads the same whatever zone the browser is in.
 * Returns "" when the value cannot be read, so a caller shows nothing rather
 * than "Invalid Date".
 */
export const formatScheduledAt = (value: string | null | undefined): string => {
  const ms = Date.parse((value ?? "").trim());
  if (!Number.isFinite(ms)) return "";
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Bangkok",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(new Date(ms));
  const at = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((p) => p.type === type)?.value ?? "";
  return `${at("day")}/${at("month")}/${at("year")} ${at("hour")}:${at("minute")}`;
};
